import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { forms } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { LoadFailure } from '../LoadFailure.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import { FormAssignment } from './FormAssignment.js';
import { FormDraftEditor } from './FormDraftEditor.js';
import {
  type VersionDraft,
  draftFromVersion,
  emptyDraft,
  mappingApprovalConsequence,
  revisionIntent,
  toDraftInput,
  validateMapping,
} from './formsAuthoring.js';
import {
  CADENCE_READING,
  DECISION_READING,
  INSTRUMENT_READING,
  SUBJECT_READING,
  VERSION_STATE_READING,
  authorshipReading,
  authorshipSentence,
  eventReading,
  formatWhen,
  mappingPill,
  mappingReading,
  mappingSentence,
  messageOf,
  needsAiReviewStatement,
  publishability,
  publishabilitySentence,
  publishedVersion,
} from './formsLogic.js';

/**
 * One form: every version, every approval, every signal mapping, and the
 * history that joins them.
 *
 * THE TWO BUTTONS ARE TWO BUTTONS ON PURPOSE. Approve records a person's
 * decision against this version's exact content hash; Publish puts an approved
 * version into use. Merging them into "Approve and publish" would let one click
 * do the thing the domain spends a migration making structurally impossible to
 * do by accident — and the server would not stop it, because approving really
 * does make publishing legal. What stops it is that they are two decisions and
 * a person makes them one at a time.
 *
 * AN AI-DRAFTED VERSION ASKS FOR MORE. When `authorship` is not `human`, the
 * approval body must carry `aiReviewStatement`, and the form here says what that
 * statement is FOR: the approver is attesting that they reviewed what a model
 * produced. The field is required by the server; making it required here too is
 * so the approver reads the sentence before, rather than after, a 400.
 */
export function FormDetail() {
  const params = useParams({ strict: false }) as { formId?: string };
  const formId = params.formId ?? '';
  const qc = useQueryClient();
  const navigate = useNav();
  const { has } = usePermissions();
  const canAuthor = has(forms.FORMS_AUTHOR);
  const canApprove = has(forms.FORMS_APPROVE);
  const canMapSignals = has(forms.FORMS_MAP_SIGNAL);

  const [decidingId, setDecidingId] = useState<string | null>(null);
  /**
   * THE AUTHORING SESSION. `revising` is the SOURCE version's id, and null means
   * "the first version of this form". There is deliberately no third mode for
   * "edit this version": a published version is immutable, an approved one is
   * bound to the content hash its approval names, and the domain's answer to
   * both is version n+1.
   */
  const [authoring, setAuthoring] = useState<{
    revising: string | null;
    draft: VersionDraft;
  } | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [mappingDraft, setMappingDraft] = useState<{
    versionId: string;
    fieldKey: string;
    matchOperator: forms.FormMatchOperator;
    matchValue: string;
    signalSource: forms.FormSignalSource;
    signalKey: string;
    signalState: forms.FormSignalState;
    automation: forms.FormAutomation;
  } | null>(null);
  const [decision, setDecision] = useState<forms.FormApprovalDecision>('approved');
  const [statement, setStatement] = useState('');
  const [aiReviewStatement, setAiReviewStatement] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['forms', 'detail', formId],
    queryFn: () => forms.detail(formId),
    enabled: formId !== '',
  });

  function resetDecision() {
    setDecidingId(null);
    setStatement('');
    setAiReviewStatement('');
    setFormError(null);
  }

  const record = useMutation({
    mutationFn: (versionId: string) =>
      forms.recordDecision(versionId, {
        decision,
        statement: statement.trim(),
        ...(aiReviewStatement.trim() === '' ? {} : { aiReviewStatement: aiReviewStatement.trim() }),
      }),
    onSuccess: () => {
      resetDecision();
      void qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const publish = useMutation({
    mutationFn: (versionId: string) => forms.publish(versionId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['forms'] }),
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const review = useMutation({
    mutationFn: (versionId: string) => forms.submitForReview(versionId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['forms'] }),
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const draft = useMutation({
    mutationFn: (input: { revising: string | null; body: forms.DraftVersionInput }) =>
      input.revising === null
        ? forms.draftVersion(formId, input.body)
        : forms.reviseVersion(input.revising, input.body),
    onSuccess: () => {
      setAuthoring(null);
      setFormError(null);
      void qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const propose = useMutation({
    mutationFn: (input: { versionId: string; body: forms.ProposeMappingInput }) =>
      forms.proposeMapping(input.versionId, input.body),
    onSuccess: () => {
      setMappingDraft(null);
      setFormError(null);
      void qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const approveMapping = useMutation({
    mutationFn: (mappingId: string) =>
      forms.approveMapping(mappingId, { statement: statement.trim() }),
    onSuccess: () => {
      resetDecision();
      void qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  if (detail.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 1000 }}>
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <LoadFailure
          error={detail.error}
          message="We couldn’t load this form just now. It may also have been removed, or belong to a different organization."
          onRetry={() => void detail.refetch()}
          mainRead
        />
      </div>
    );
  }

  const { definition, versions, approvals, mappings, history } = detail.data;
  const live = publishedVersion(versions);

  const versionColumns: Column<forms.FormVersionView>[] = [
    { key: 'n', header: 'Version', numeric: true, render: (v) => `v${v.versionNumber}` },
    {
      key: 'state',
      header: 'State',
      render: (v) => {
        const reading = VERSION_STATE_READING[v.state];
        return (
          <Pill variant={reading.variant} dot>
            {reading.label}
          </Pill>
        );
      },
    },
    {
      key: 'author',
      header: 'Author',
      render: (v) => {
        const reading = authorshipReading(v);
        switch (reading.kind) {
          case 'person':
            return reading.name;
          case 'ai':
            // NOT "Unknown". The null author IS the disclosure.
            return (
              <span>
                {reading.assisted ? 'Model-assisted' : 'Model-written'}
                <br />
                <span className="rn-muted">No human author recorded</span>
              </span>
            );
          case 'unresolved':
            return <span className="rn-muted">Author not resolved</span>;
        }
      },
    },
    { key: 'fields', header: 'Questions', numeric: true, render: (v) => String(v.fields.length) },
    {
      key: 'attestation',
      header: 'Attestation',
      render: (v) => (v.requiresAttestation ? 'Required' : 'Not required'),
    },
    { key: 'cadence', header: 'Repeats', render: (v) => CADENCE_READING[v.defaultCadence] },
    { key: 'published', header: 'Published', render: (v) => formatWhen(v.publishedAt) },
    {
      key: 'actions',
      header: '',
      render: (v) => {
        const p = publishability(v);
        return (
          <span className="rn-row" style={{ gap: 'var(--space-8)' }}>
            {canAuthor && v.state === 'draft' ? (
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={review.isPending}
                onClick={() => review.mutate(v.id)}
              >
                Send for review
              </button>
            ) : null}
            {canApprove && v.state === 'review' ? (
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setDecidingId(v.id);
                  setDecision('approved');
                  setStatement('');
                  setAiReviewStatement('');
                  setFormError(null);
                }}
              >
                Decide
              </button>
            ) : null}
            {canApprove && p.kind === 'publishable' ? (
              <button
                type="button"
                className="rn-btn"
                disabled={publish.isPending}
                onClick={() => publish.mutate(v.id)}
              >
                Publish
              </button>
            ) : null}
            {/* REVISE, NEVER EDIT — on every version including a published one,
                because "change this question" is a real thing to want and the
                only honest way to do it is version n+1. The label carries the
                number so the press is not ambiguous about what it produces. */}
            {canAuthor ? (
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setAuthoring({ revising: v.id, draft: draftFromVersion(v) });
                  setFormError(null);
                }}
              >
                Revise into v{v.versionNumber + 1}
              </button>
            ) : null}
          </span>
        );
      },
    },
  ];

  const approvalColumns: Column<forms.FormApprovalView>[] = [
    {
      key: 'decision',
      header: 'Decision',
      render: (a) => {
        const reading = DECISION_READING[a.decision];
        return (
          <Pill variant={reading.variant} dot>
            {reading.label}
          </Pill>
        );
      },
    },
    {
      key: 'subject',
      header: 'Of',
      render: (a) => (a.subjectKind === 'form_version' ? 'A version' : 'A signal mapping'),
    },
    { key: 'approver', header: 'By', render: (a) => a.approver.displayName },
    { key: 'statement', header: 'Statement', render: (a) => a.statement },
    {
      key: 'ai',
      header: 'Model review',
      // Null here means THERE WAS NO MODEL AUTHORSHIP TO REVIEW — the server
      // requires the statement whenever there was. It does not mean an approver
      // skipped it, and the copy has to say which.
      render: (a) =>
        a.aiReviewStatement === null ? (
          <span className="rn-muted">
            {a.approvedAuthorship === 'human' ? 'Written by a person' : 'Not recorded'}
          </span>
        ) : (
          a.aiReviewStatement
        ),
    },
    { key: 'when', header: 'When', render: (a) => formatWhen(a.recordedAt) },
  ];

  const mappingColumns: Column<forms.FormSignalMappingView>[] = [
    { key: 'field', header: 'Answer to', render: (m) => m.fieldKey },
    {
      key: 'match',
      header: 'When it',
      render: (m) =>
        forms.operatorTakesValue(m.matchOperator)
          ? `${m.matchOperator.replace(/_/g, ' ')} ${m.matchValue ?? ''}`.trim()
          : m.matchOperator.replace(/_/g, ' '),
    },
    { key: 'signal', header: 'Signal', render: (m) => `${m.signalKey} → ${m.signalState}` },
    {
      key: 'state',
      header: 'Effect',
      render: (m) => {
        const reading = mappingReading(m);
        const pill = mappingPill(reading);
        return (
          <span>
            <Pill variant={pill.variant} dot>
              {pill.label}
            </Pill>
            <br />
            <span className="rn-muted">{mappingSentence(reading)}</span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (m) =>
        canMapSignals && m.mappingApprovalId === null && m.active ? (
          <button
            type="button"
            className="rn-btn-quiet"
            onClick={() => {
              setDecidingId(`mapping:${m.id}`);
              setStatement('');
              setFormError(null);
            }}
          >
            Approve
          </button>
        ) : null,
    },
  ];

  const decidingVersion =
    decidingId !== null && !decidingId.startsWith('mapping:')
      ? versions.find((v) => v.id === decidingId)
      : undefined;
  const decidingMappingId = decidingId?.startsWith('mapping:')
    ? decidingId.slice('mapping:'.length)
    : null;
  const decidingMapping =
    decidingMappingId === null ? undefined : mappings.find((m) => m.id === decidingMappingId);
  /**
   * The version a revision is being written FROM.
   *
   * `undefined` when this is a first draft. It is resolved here rather than
   * inside the render so the editor can never be handed a revision intent for a
   * version that is no longer in the payload — a refetch that retires a row must
   * close the editor, not describe the wrong version.
   */
  /**
   * The versions a mapping may be attached to.
   *
   * NOT "every version". A mapping is proposed against a version's field list,
   * so a version with no fields can carry none — and a retired version can
   * carry one that will never match anything, which is a mapping nobody should
   * be encouraged to author. Draft, review, approved and published all qualify;
   * retired does not.
   */
  const publishableVersionsForMapping = versions
    .filter((v) => v.state !== 'retired' && v.fields.length > 0)
    .sort((a, b) => b.versionNumber - a.versionNumber);

  const revisingVersion =
    authoring !== null && authoring.revising !== null
      ? versions.find((v) => v.id === authoring.revising)
      : undefined;

  const requiresAiStatement =
    decidingVersion !== undefined &&
    decision === 'approved' &&
    needsAiReviewStatement(decidingVersion);

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title={definition.title}
        blurb={`${INSTRUMENT_READING[definition.instrumentKind]} · can be about ${definition.subjectKinds
          .map((s) => SUBJECT_READING[s].toLowerCase())
          .join(', ')}${definition.purpose ? ` · ${definition.purpose}` : ''}`}
      />

      <div>
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => navigate('/compliance/forms')}
        >
          {'‹'} All forms
        </button>
      </div>

      <SettingsCard>
        <div className="cmp-control-head">
          <h2 className="rn-card-title">What is in use</h2>
          {live ? (
            <Pill variant="good" dot>
              v{live.versionNumber} published
            </Pill>
          ) : (
            <Pill variant="look" dot>
              Nothing published
            </Pill>
          )}
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {live
            ? `${authorshipSentence(authorshipReading(live))} Published ${formatWhen(live.publishedAt)}. Responses already recorded keep answering the version they were given, whatever happens to this one.`
            : 'No version of this form is published, so nobody can be asked it. That is a real state and not a loading failure — a form is written, reviewed, approved and only then put into use.'}
        </p>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Versions</h2>
        {formError ? (
          <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
            {formError}
          </p>
        ) : null}
        {versions.length === 0 ? (
          <SuccessEmpty
            heading="No versions yet"
            message="This form has been created but nothing has been drafted into it."
          />
        ) : (
          <TableCard
            columns={versionColumns}
            rows={[...versions].sort((a, b) => b.versionNumber - a.versionNumber)}
            rowKey={(v) => v.id}
            caption="Versions of this form"
          />
        )}

        {canAuthor && authoring === null ? (
          <div className="rn-row" style={{ gap: 'var(--space-8)', marginTop: 'var(--space-12)' }}>
            <button
              type="button"
              className="rn-btn"
              onClick={() => {
                setAuthoring({ revising: null, draft: emptyDraft() });
                setFormError(null);
              }}
            >
              {versions.length === 0 ? 'Write the first version' : 'Write another version'}
            </button>
          </div>
        ) : null}

        {canAuthor && authoring !== null ? (
          <FormDraftEditor
            draft={authoring.draft}
            onChange={(next) => setAuthoring({ ...authoring, draft: next })}
            intent={revisingVersion === undefined ? undefined : revisionIntent(revisingVersion)}
            busy={draft.isPending}
            serverError={formError}
            onCancel={() => {
              setAuthoring(null);
              setFormError(null);
            }}
            onSubmit={() =>
              draft.mutate({
                revising: authoring.revising,
                body: toDraftInput(authoring.draft),
              })
            }
          />
        ) : null}

        {/* Why a version that LOOKS ready has no Publish button. Stated for the
            versions on screen rather than as a general note, because "why can I
            not publish this one" is a question about a specific row. */}
        {versions.some((v) => publishability(v).kind === 'needs-approval') ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            {publishabilitySentence({ kind: 'needs-approval', state: 'approved' })}
          </p>
        ) : null}

        {decidingVersion !== undefined && canApprove ? (
          <form
            className="rn-col"
            style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (
                statement.trim() !== '' &&
                (!requiresAiStatement || aiReviewStatement.trim() !== '')
              ) {
                record.mutate(decidingVersion.id);
              }
            }}
          >
            <h3 className="rn-card-title">Decide on v{decidingVersion.versionNumber}</h3>
            <p className="rn-muted">{authorshipSentence(authorshipReading(decidingVersion))}</p>
            <SettingsField label="Decision">
              <select
                value={decision}
                onChange={(e) => setDecision(e.currentTarget.value as forms.FormApprovalDecision)}
              >
                <option value="approved">Approve</option>
                <option value="changes_requested">Ask for changes</option>
                <option value="rejected">Reject</option>
              </select>
            </SettingsField>
            <SettingsField
              label="Your statement"
              hint="Recorded against this exact wording and kept. It is what an auditor reads when they ask why this question was asked."
            >
              <textarea
                rows={3}
                value={statement}
                onChange={(e) => setStatement(e.currentTarget.value)}
              />
            </SettingsField>
            {requiresAiStatement ? (
              <SettingsField
                label="What you checked in the model’s draft"
                hint="Required, because no person wrote this version. Approving it is your attestation that you read what the model produced and are content for it to be asked of a person."
              >
                <textarea
                  rows={3}
                  value={aiReviewStatement}
                  onChange={(e) => setAiReviewStatement(e.currentTarget.value)}
                />
              </SettingsField>
            ) : null}
            <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <button
                type="submit"
                className="rn-btn"
                disabled={
                  record.isPending ||
                  statement.trim() === '' ||
                  (requiresAiStatement && aiReviewStatement.trim() === '')
                }
              >
                Record the decision
              </button>
              <button type="button" className="rn-btn-quiet" onClick={resetDecision}>
                Cancel
              </button>
            </div>
            <p className="rn-muted">
              Approving does not publish. It records that you approved this wording; putting it into
              use is a second, separate press.
            </p>
          </form>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Who is asked this</h2>
        <p className="rn-muted">
          An assignment records that this instrument is owed about somebody by some date. Assigning
          is not publishing: only a published version can actually be answered, so an assignment
          against a form with nothing published is an obligation nobody can discharge yet.
        </p>
        {canAuthor && !assigning ? (
          <div className="rn-row" style={{ gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
            <button type="button" className="rn-btn-quiet" onClick={() => setAssigning(true)}>
              Assign this form
            </button>
          </div>
        ) : null}
        {canAuthor && assigning ? (
          <FormAssignment
            definition={definition}
            onDone={() => setAssigning(false)}
            onCancel={() => setAssigning(false)}
          />
        ) : null}
        {live === undefined ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            Nothing is published, so anybody assigned this form would have no version to answer.
          </p>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Decisions</h2>
        {approvals.length === 0 ? (
          <SuccessEmpty
            heading="Nobody has decided anything yet"
            message="No version of this form has been approved, rejected or sent back."
          />
        ) : (
          <TableCard
            columns={approvalColumns}
            rows={[...approvals]}
            rowKey={(a) => a.id}
            caption="Approval decisions"
          />
        )}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">What answers do</h2>
        <p className="rn-muted">
          A mapping turns a particular answer into an operational signal. Until somebody approves
          it, it is inert — it matches nothing and raises nothing — and the response screen reports
          the matches it would have made, so the silence is visible rather than mysterious.
        </p>
        {mappings.length === 0 ? (
          <SuccessEmpty
            heading="Answers do nothing on their own"
            message="No mapping is set up, so answering this form records the answers and raises nothing."
          />
        ) : (
          <TableCard
            columns={mappingColumns}
            rows={[...mappings]}
            rowKey={(m) => m.id}
            caption="Signal mappings"
          />
        )}

        {canAuthor && mappingDraft === null && publishableVersionsForMapping.length > 0 ? (
          <div className="rn-row" style={{ gap: 'var(--space-8)', marginTop: 'var(--space-12)' }}>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                const target = publishableVersionsForMapping[0];
                if (target === undefined) return;
                setMappingDraft({
                  versionId: target.id,
                  fieldKey: target.fields[0]?.fieldKey ?? '',
                  matchOperator: 'equals',
                  matchValue: '',
                  signalSource: 'customer_defined',
                  signalKey: 'custom:',
                  signalState: 'active',
                  automation: 'signal_only',
                });
                setFormError(null);
              }}
            >
              Propose a mapping
            </button>
          </div>
        ) : null}

        {canAuthor && mappingDraft !== null ? (
          <ProposeMappingForm
            versions={publishableVersionsForMapping}
            value={mappingDraft}
            onChange={setMappingDraft}
            busy={propose.isPending}
            serverError={formError}
            onCancel={() => {
              setMappingDraft(null);
              setFormError(null);
            }}
            onSubmit={() => {
              const takesValue = forms.operatorTakesValue(mappingDraft.matchOperator);
              propose.mutate({
                versionId: mappingDraft.versionId,
                body: {
                  fieldKey: mappingDraft.fieldKey,
                  matchOperator: mappingDraft.matchOperator,
                  ...(takesValue ? { matchValue: mappingDraft.matchValue.trim() } : {}),
                  signalSource: mappingDraft.signalSource,
                  signalKey: mappingDraft.signalKey.trim(),
                  signalState: mappingDraft.signalState,
                  automation: mappingDraft.automation,
                },
              });
            }}
          />
        ) : null}

        {decidingMappingId !== null && canMapSignals ? (
          <form
            className="rn-col"
            style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (statement.trim() !== '') approveMapping.mutate(decidingMappingId);
            }}
          >
            {/* A SIGNAL MAPPING IS THE SEAM WHERE AN ANSWER SOMEBODY TYPED
                BECOMES SOMETHING PHIBIAN REASONS ABOUT, so approving one is a
                decision about automation and is written like one: what will
                start happening, in the tense it will happen in, above the
                statement box rather than after the press. */}
            {decidingMapping === undefined ? null : (
              <p>{mappingApprovalConsequence(decidingMapping)}</p>
            )}
            <SettingsField
              label="Why this mapping should fire"
              hint="Recorded with your name. Approving it is what makes a matching answer raise something."
            >
              <textarea
                rows={3}
                value={statement}
                onChange={(e) => setStatement(e.currentTarget.value)}
              />
            </SettingsField>
            <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <button
                type="submit"
                className="rn-btn"
                disabled={approveMapping.isPending || statement.trim() === ''}
              >
                Approve the mapping
              </button>
              <button type="button" className="rn-btn-quiet" onClick={resetDecision}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">History</h2>
        {history.length === 0 ? (
          <SuccessEmpty
            heading="Nothing has happened"
            message="No events are recorded for this form."
          />
        ) : (
          <ol
            className="rn-col"
            style={{ gap: 'var(--space-12)', paddingLeft: 0, listStyle: 'none' }}
          >
            {[...history].map((event) => (
              <li key={event.id} style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <span>
                  <strong>{eventReading(event.kind)}</strong>
                  {event.fromState && event.toState
                    ? ` · ${event.fromState} → ${event.toState}`
                    : null}
                </span>
                <span className="rn-muted">
                  {formatWhen(event.occurredAt)} ·{' '}
                  {/* A null actor is not an unknown person: the server pairs
                      `actor` with `actorKind`, so null means the platform acted. */}
                  {event.actorKind === 'user'
                    ? (event.actor?.displayName ?? 'A person whose name we could not resolve')
                    : event.actorKind === 'phibian'
                      ? 'Phibian'
                      : 'The system'}
                </span>
                {event.detail ? <span className="rn-muted">{event.detail}</span> : null}
              </li>
            ))}
          </ol>
        )}
      </SettingsCard>
    </div>
  );
}

interface MappingDraft {
  versionId: string;
  fieldKey: string;
  matchOperator: forms.FormMatchOperator;
  matchValue: string;
  signalSource: forms.FormSignalSource;
  signalKey: string;
  signalState: forms.FormSignalState;
  automation: forms.FormAutomation;
}

const MATCH_OPERATOR_READING: Record<forms.FormMatchOperator, string> = {
  equals: 'is exactly',
  not_equals: 'is anything but',
  includes: 'contains',
  greater_than: 'is greater than',
  less_than: 'is less than',
  is_blank: 'is left blank',
  is_not_blank: 'is answered at all',
};

/**
 * PROPOSING a signal mapping. Proposing, never enabling.
 *
 * A proposed mapping is INERT by construction — `form_signal_emission
 * .mapping_approval_id` is NOT NULL, so there is no emission row that can name
 * an unapproved mapping — and this form says so where the author can read it,
 * because "I set it up and nothing happened" is otherwise indistinguishable
 * from a fault.
 *
 * THE SIGNAL VOCABULARY IS HALF-KNOWABLE HERE, AND THE FORM SAYS WHICH HALF. A
 * `customer_defined` key must be written `custom:<something>` and that pattern
 * is shipped in the client, so it is checked here. A `library` key must name an
 * `OPERATIONAL_SIGNALS` id, and THAT LIST IS NOT ON THE WIRE — no route serves
 * it. Rather than shipping a copy of it that would drift silently, this form
 * takes the key as text, says plainly that the server holds the list, and shows
 * the server's own refusal, which names the key it rejected.
 */
function ProposeMappingForm({
  versions,
  value,
  onChange,
  busy,
  serverError,
  onSubmit,
  onCancel,
}: {
  versions: readonly forms.FormVersionView[];
  value: MappingDraft;
  onChange: (next: MappingDraft) => void;
  busy: boolean;
  serverError: string | null;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const [showProblems, setShowProblems] = useState(false);
  const version = versions.find((v) => v.id === value.versionId);
  const problems = validateMapping(value);
  const takesValue = forms.operatorTakesValue(value.matchOperator);

  return (
    <form
      className="rn-col"
      style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
      onSubmit={(e) => {
        e.preventDefault();
        setShowProblems(true);
        if (problems.length === 0) onSubmit();
      }}
    >
      <h3 className="rn-card-title">Propose a mapping</h3>
      <p className="rn-muted">
        A mapping is proposed inert. It matches nothing and raises nothing until somebody with the
        authority to approve it does so, and approving it is a decision about automation rather than
        a switch.
      </p>

      <SettingsField label="On which version">
        <select
          value={value.versionId}
          onChange={(e) => {
            const next = versions.find((v) => v.id === e.currentTarget.value);
            onChange({
              ...value,
              versionId: e.currentTarget.value,
              fieldKey: next?.fields[0]?.fieldKey ?? '',
            });
          }}
        >
          {versions.map((v) => (
            <option key={v.id} value={v.id}>
              v{v.versionNumber} · {v.state}
            </option>
          ))}
        </select>
      </SettingsField>

      <SettingsField label="When the answer to">
        <select
          value={value.fieldKey}
          onChange={(e) => onChange({ ...value, fieldKey: e.currentTarget.value })}
        >
          {(version?.fields ?? []).map((f) => (
            <option key={f.fieldKey} value={f.fieldKey}>
              {f.label} ({f.fieldKey})
            </option>
          ))}
        </select>
      </SettingsField>

      <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
        <SettingsField label="Comparison">
          <select
            value={value.matchOperator}
            onChange={(e) =>
              onChange({
                ...value,
                matchOperator: e.currentTarget.value as forms.FormMatchOperator,
                // Clearing the value when the operator stops taking one is not
                // tidiness: the server refuses `is_blank` WITH a value, and
                // leaving a stale one behind turns a working form into a 400.
                matchValue: forms.operatorTakesValue(
                  e.currentTarget.value as forms.FormMatchOperator,
                )
                  ? value.matchValue
                  : '',
              })
            }
          >
            {(Object.keys(MATCH_OPERATOR_READING) as forms.FormMatchOperator[]).map((op) => (
              <option key={op} value={op}>
                {MATCH_OPERATOR_READING[op]}
              </option>
            ))}
          </select>
        </SettingsField>
        {takesValue ? (
          <SettingsField label="This value">
            <input
              type="text"
              value={value.matchValue}
              onChange={(e) => onChange({ ...value, matchValue: e.currentTarget.value })}
            />
          </SettingsField>
        ) : null}
      </div>

      <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
        <SettingsField label="Signal">
          <select
            value={value.signalSource}
            onChange={(e) =>
              onChange({
                ...value,
                signalSource: e.currentTarget.value as forms.FormSignalSource,
                signalKey: e.currentTarget.value === 'customer_defined' ? 'custom:' : '',
              })
            }
          >
            <option value="customer_defined">One this organization defines</option>
            <option value="library">One from the Operational Library</option>
          </select>
        </SettingsField>
        <SettingsField
          label="Signal key"
          hint={
            value.signalSource === 'customer_defined'
              ? 'Must start `custom:` so it can never be mistaken for a signal Phibian reasons over.'
              : 'Must name an Operational Library signal. This screen cannot list them — no route serves the catalog — so the server checks it and names the key if it does not know it.'
          }
        >
          <input
            type="text"
            value={value.signalKey}
            spellCheck={false}
            onChange={(e) => onChange({ ...value, signalKey: e.currentTarget.value })}
          />
        </SettingsField>
      </div>

      <div className="rn-row" style={{ gap: 'var(--space-12)', flexWrap: 'wrap' }}>
        <SettingsField label="It should record the signal as">
          <select
            value={value.signalState}
            onChange={(e) =>
              onChange({ ...value, signalState: e.currentTarget.value as forms.FormSignalState })
            }
          >
            <option value="active">Active — this is now true</option>
            <option value="cleared">Cleared — this is no longer true</option>
          </select>
        </SettingsField>
        <SettingsField
          label="And then"
          hint="Part of what an approver approves. Changing it later breaks the approval and the mapping goes inert again."
        >
          <select
            value={value.automation}
            onChange={(e) =>
              onChange({ ...value, automation: e.currentTarget.value as forms.FormAutomation })
            }
          >
            <option value="signal_only">Record the signal, and nothing else</option>
            <option value="raise_task">Record the signal and raise a task</option>
          </select>
        </SettingsField>
      </div>

      {showProblems && problems.length > 0 ? (
        <div role="alert" className="rn-col" style={{ gap: 'var(--space-4)' }}>
          <ul style={{ paddingLeft: 'var(--space-16)' }}>
            {problems.map((problem, i) => (
              <li key={`${problem.where}-${i}`}>
                <strong>{problem.where}</strong> — {problem.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {serverError ? (
        <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
          {serverError}
        </p>
      ) : null}

      <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
        <button type="submit" className="rn-btn" disabled={busy}>
          Propose it
        </button>
        <button type="button" className="rn-btn-quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
